import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, post, put, del } from "../../config/axios";
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toastMessage } from "../../utils/toastMessage";

const Questions = () => {
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState({ 
    question: "", 
    option1: "", 
    option2: "" 
  });
  const [deleteData, setDeleteData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const columns = [
    { 
      id: "sno", 
      label: "Sno", 
      minWidth: 70, 
      align: "center",
      format: (value, row, index) => index + 1
    },
    { 
      id: "question", 
      label: "Question Text", 
      minWidth: 300, 
      align: "left" 
    },
    { 
      id: "option1", 
      label: "Answer Option 1", 
      minWidth: 150, 
      align: "left" 
    },
    { 
      id: "option2", 
      label: "Answer Option 2", 
      minWidth: 150, 
      align: "left" 
    },
    { 
      id: "lastEditDate", 
      label: "Last Edit Date", 
      minWidth: 120, 
      align: "center",
      format: (value) => value ? new Date(value).toLocaleDateString('en-GB') : "–"
    },
    { 
      id: "action", 
      label: "Actions", 
      minWidth: 120, 
      align: "center" 
    },
  ];

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await get("/api/dashboard/cbt/questions/screening");
      console.log("Screening questions response:", res);
      
      if (res?.success && res?.questions) {
        const mappedData = res.questions.map((item, index) => ({
          _id: item._id,
          question: item.question || "",
          option1: item.options?.[0] || "Yes",
          option2: item.options?.[1] || "No",
          lastEditDate: item.updatedAt || item.createdAt,
          sno: index + 1,
          action: {
            edit: true,
            delete: true
          }
        }));
        setTableData(mappedData);
        setTotalData(mappedData.length);
      } else {
        setTableData([]);
        setTotalData(0);
      }
    } catch (error) {
      console.error("Error fetching screening questions:", error);
      toastMessage("Failed to fetch screening questions", "error");
      setTableData([]);
      setTotalData(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleEdit = (row) => {
    setEditData({
      _id: row._id,
      question: row.question,
      option1: row.option1,
      option2: row.option2
    });
    setEditModal(true);
  };

  const handleDelete = (row) => {
    setDeleteData(row);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteData) return;
    
    try {
      const response = await del(`/api/dashboard/cbt/questions/screening/${deleteData._id}`);
      
      if (response?.success) {
        toastMessage("Question deleted successfully", "success");
        setDeleteModal(false);
        setDeleteData(null);
        fetchQuestions();
      } else {
        toastMessage(response?.message || "Failed to delete question", "error");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      toastMessage("Failed to delete question", "error");
    }
  };

  const handleSubmit = async (formData, isEditing = false, id = null) => {
    try {
      if (isEditing) {
        // Update existing question
        const response = await put(`/api/dashboard/cbt/questions/screening/${id}`, {
          question: formData.question,
          options: [formData.option1, formData.option2]
        });
        
        if (response?.success) {
          toastMessage("Question updated successfully", "success");
          setEditModal(false);
          setEditData({ question: "", option1: "", option2: "" });
          fetchQuestions();
        } else {
          toastMessage(response?.message || "Failed to update question", "error");
        }
      } else {
        // Create new question
        const response = await post("/api/dashboard/cbt/questions/screening", {
          question: formData.question,
          options: [formData.option1, formData.option2]
        });
        
        if (response?.success) {
          toastMessage("Question created successfully", "success");
          setIsModalOpen(false);
          setEditData({ question: "", option1: "", option2: "" });
          fetchQuestions();
        } else {
          toastMessage(response?.message || "Failed to create question", "error");
        }
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toastMessage("Failed to save question", "error");
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setEditData({ question: "", option1: "Yes", option2: "No" });
      setIsModalOpen(true);
    } else if (type === "edit") {
      setEditModal(true);
      setEditData(dataForEdit);
    }
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setEditModal(false);
      setEditData({ question: "", option1: "", option2: "" });
    }
  };

  const handleChange = (page) => {
    setCurrentPage(page);
    setPage(page);
  };

  const filteredData = useMemo(() => {
    if (!search) return tableData;
    return tableData.filter((item) =>
      item.question.toLowerCase().includes(search.toLowerCase()) ||
      item.option1.toLowerCase().includes(search.toLowerCase()) ||
      item.option2.toLowerCase().includes(search.toLowerCase())
    );
  }, [tableData, search]);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              CBT Questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage screening questions for CBT sessions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModal("add")}
          >
            Add Question
          </Button>
        </Box>

        {/* Notes Section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Notes:</strong> Changing the text here does not affect the logic of the questions; it only updates the content that the user sees. We can add more questions and logic for those questions also be same.
          </Typography>
        </Box>

        {/* Questions Table */}
        <CustomTable
          columns={columns}
          data={filteredData}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          currentPage={currentPage}
          totalData={totalData}
          perPage={perPage}
          onPageChange={handleChange}
          searchPlaceholder="Search questions..."
          handleEdit={handleEdit}
          handleDelete={handleDelete}
        />

        {/* Add Question Modal */}
        <Dialog open={isModalOpen} onClose={() => closeModal("add")} maxWidth="md" fullWidth>
          <DialogTitle>Add New Question</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Question Text"
                value={editData.question}
                onChange={(e) => setEditData({ ...editData, question: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Answer Option 1"
                value={editData.option1}
                onChange={(e) => setEditData({ ...editData, option1: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Answer Option 2"
                value={editData.option2}
                onChange={(e) => setEditData({ ...editData, option2: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeModal("add")}>Cancel</Button>
            <Button 
              onClick={() => handleSubmit(editData, false)} 
              variant="contained"
              disabled={!editData.question || !editData.option1 || !editData.option2}
            >
              Add Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Question Modal */}
        <Dialog open={editModal} onClose={() => closeModal("edit")} maxWidth="md" fullWidth>
          <DialogTitle>Edit Question</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Question Text"
                value={editData.question}
                onChange={(e) => setEditData({ ...editData, question: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Answer Option 1"
                value={editData.option1}
                onChange={(e) => setEditData({ ...editData, option1: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Answer Option 2"
                value={editData.option2}
                onChange={(e) => setEditData({ ...editData, option2: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeModal("edit")}>Cancel</Button>
            <Button 
              onClick={() => handleSubmit(editData, true, editData._id)} 
              variant="contained"
              disabled={!editData.question || !editData.option1 || !editData.option2}
            >
              Update Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal} onClose={() => setDeleteModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Question</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Are you sure you want to delete this question?
            </Typography>
            {deleteData && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Question:
                </Typography>
                <Typography variant="body2">
                  {deleteData.question}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModal(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              variant="contained"
              color="error"
            >
              Delete Question
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Questions;
